import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { TrendingUp, Users, Activity, Clock, Download } from 'lucide-react';

const COLORS = ['#0066cc', '#4a90e2', '#82ca9d', '#ffc658', '#ff8042', '#8884d8'];

const StatisticsCharts = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    periode: 'month',
    sexe: '',
    age_min: '',
    age_max: '',
    protocole: ''
  });

  useEffect(() => {
    fetchStats();
  }, [filters.periode, filters.sexe, filters.age_min, filters.age_max, filters.protocole]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.periode) params.append('periode', filters.periode);
      if (filters.sexe) params.append('sexe', filters.sexe);
      if (filters.age_min) params.append('age_min', filters.age_min);
      if (filters.age_max) params.append('age_max', filters.age_max);
      if (filters.protocole) params.append('protocole', filters.protocole);

      const response = await axios.get(`${API}/stats/advanced?${params.toString()}`);
      setStats(response.data);
      toast.success('📊 Statistiques mises à jour');
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    toast.info('Export PDF en cours de développement...');
  };

  const handleExportCSV = () => {
    if (!stats) return;
    
    // Simple CSV export
    const csvData = [
      ['Statistiques Omevita'],
      ['Total Perfusions', stats.total_perfusions],
      ['Durée Moyenne', `${stats.average_duration} min`],
      [''],
      ['Top Protocoles'],
      ['Nom', 'Nombre'],
      ...stats.top_protocols.map(p => [p.nom, p.count])
    ];

    const csv = csvData.map(row => row.join(',')).join('\\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'statistiques-omevita.csv';
    a.click();
    
    toast.success('✅ Export CSV réussi');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Prepare chart data
  const genderChartData = Object.entries(stats?.gender_distribution || {}).map(([key, value]) => ({
    name: key === 'homme' ? 'Hommes' : key === 'femme' ? 'Femmes' : 'Autre',
    value: value
  })).filter(item => item.value > 0);

  const ageChartData = Object.entries(stats?.age_distribution || {}).map(([key, value]) => ({
    name: key,
    value: value
  })).filter(item => item.value > 0);

  const monthlyChartData = stats?.monthly_evolution || [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
          <CardDescription>Personnalisez l'affichage des statistiques</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Période</Label>
              <select
                value={filters.periode}
                onChange={(e) => setFilters({ ...filters, periode: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="day">Jour</option>
                <option value="week">Semaine</option>
                <option value="month">Mois</option>
                <option value="year">Année</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Sexe</Label>
              <select
                value={filters.sexe}
                onChange={(e) => setFilters({ ...filters, sexe: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Tous</option>
                <option value="homme">Homme</option>
                <option value="femme">Femme</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Âge Min</Label>
              <input
                type="number"
                value={filters.age_min}
                onChange={(e) => setFilters({ ...filters, age_min: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="18"
              />
            </div>
            <div className="space-y-2">
              <Label>Âge Max</Label>
              <input
                type="number"
                value={filters.age_max}
                onChange={(e) => setFilters({ ...filters, age_max: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="100"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={handleExportCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={handleExportPDF}>
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Perfusions</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_perfusions || 0}</div>
            <p className="text-xs text-muted-foreground">
              Sur la période sélectionnée
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Durée Moyenne</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.average_duration || 0} min</div>
            <p className="text-xs text-muted-foreground">
              Par séance de perfusion
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Protocole</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.top_protocols?.[0]?.nom || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.top_protocols?.[0]?.count || 0} utilisations
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Protocols Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Top 5 Protocoles</CardTitle>
            <CardDescription>Protocoles les plus utilisés</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats?.top_protocols || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nom" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#0066cc" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gender Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Répartition par Sexe</CardTitle>
            <CardDescription>Distribution des patients</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={genderChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {genderChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Age Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Répartition par Âge</CardTitle>
            <CardDescription>Tranches d'âge des patients</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ageChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#4a90e2" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly Evolution */}
        <Card>
          <CardHeader>
            <CardTitle>Évolution Mensuelle</CardTitle>
            <CardDescription>Nombre de perfusions par mois</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#0066cc" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations (Optional AI) */}
      {stats?.total_perfusions > 10 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-900">💡 Recommandations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-blue-800">
              • Le protocole "{stats.top_protocols[0]?.nom}" est le plus populaire avec {stats.top_protocols[0]?.count} utilisations
            </p>
            {genderChartData.length > 0 && (
              <p className="text-sm text-blue-800">
                • {genderChartData[0].name} représentent {Math.round((genderChartData[0].value / stats.total_perfusions) * 100)}% des patients
              </p>
            )}
            <p className="text-sm text-blue-800">
              • Durée moyenne de perfusion: {stats.average_duration} minutes
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StatisticsCharts;
